import { useState } from 'react';
import { EyeColorType, ColorValueHex } from '@avaturn/sdk';
import { Toast } from '~/components/atomic';
import { FullScreenLoading } from '~/components/common';
import { Header } from '~/components/common/Header';
import Asset from './Asset';
import CustomSlider from './CustomSlider';
import EyeColorPicker from './EyeColorPIcker';
import EyewearColorPicker from './EyewearColorPicker';
import * as S from './FilterAvaturn.styled';
import HairColorPicker from './HairColorPicker';
import useFilterAvaturn from './useFilterAvaturn';

export interface FilterAvaturnProps {
  onPrevStep: () => void;
  onNextStep: () => void;
}

export default function FilterAvaturn({
  onPrevStep,
  onNextStep,
}: FilterAvaturnProps) {
  const {
    isPending,
    containerRef,
    disableUi,
    sdk,
    editorLoaded,
    assetType,
    assetList,
    bodyList,
    assetTypeArray,
    handleFinishClick,
    handleSetAsset,
    handleSetBody,
    setAssetType,
    handleChangeHairColor,
    handleChangeSkinToneColor,
    handleChangeEyeColor,
    handleChangeEyeWearColor,
    handleChangeHeadProportions,
    handleChangeArmsProportions,
    handleChangeChestProportions,
  } = useFilterAvaturn({ onNextStep });

  const [selectedEyeColor, setSelectedEyeColor] =
    useState<EyeColorType>('brown');
  const [selectedHairColor, setSelectedHairColor] = useState('#A56A4F');
  const [selectedSkinTone, setSelectedSkinTone] = useState(0.0);
  const [selectedHead, setSelectedHead] = useState(-5.0);
  const [selectedArms, setSelectedArms] = useState(0.0);
  const [selectedChest, setSelectedChest] = useState(0.0);
  const [selectedEyewearColor, setSelectedEyewearColor] = useState<
    ColorValueHex | 'transparent'
  >('transparent');
  const hairColors = [
    '#000000', // Black (검은색)
    '#2F1B0D', // Dark Brown (짙은 갈색)
    '#F3D5A6', // Light Blonde (밝은 금발)
    '#A56A4F', // Auburn (붉은 갈색)
    '#D2691E', // Copper (구리색)
    '#B3B3B3', // Silver (은회색)
  ];

  const sunglassesColors = [
    '#000000', // Black (검은색)
    '#2C2C2C', // Charcoal (차콜)
    '#4B3621', // Brown (갈색)
    '#8B5A2B', // Light Brown (밝은 갈색)
    '#FFD700', // Gold (금색)
    '#B3B3B3', // Silver (은색)
    '#5E6366', // Gunmetal (건메탈)
    '#4A3F35', // Tortoise (거북이무늬)
    '#1E90FF', // Blue Tint (블루 틴트)
    '#8B0000', // Dark Red (다크 레드)
  ] as `#${string}`[];

  return (
    <>
      {isPending && <FullScreenLoading />}
      {!editorLoaded && (
        <div className='toast'>
          <Toast
            type={'system'}
            text={'If you leave this page, Your character will be deleted.'}
          />
        </div>
      )}

      <Header.Root>
        <Header.Section>
          <Header.PrevButton onPrevClick={onPrevStep} />
          <Header.Title>Generate avatar</Header.Title>
        </Header.Section>
      </Header.Root>

      <S.Container>
        <S.ViewerWrapper ref={containerRef} />
        {disableUi && sdk && editorLoaded && (
          <>
            <S.FinishButton onClick={handleFinishClick}>Finish</S.FinishButton>

            <S.AssetContainer>
              <div>
                {assetType === 'eyewear' && (
                  <>
                    <S.AssetWrapper>
                      {assetList.eyewear.map(({ id, preview }) => (
                        <Asset
                          key={id}
                          id={id}
                          imageUrl={preview}
                          handleAsset={() => handleSetAsset(id)}
                        />
                      ))}
                    </S.AssetWrapper>
                    <S.AssetWrapperFlex direction='column'>
                      <EyewearColorPicker
                        colors={sunglassesColors}
                        selectedColor={selectedEyewearColor}
                        onChange={(color) =>
                          handleChangeEyeWearColor(
                            color,
                            setSelectedEyewearColor,
                          )
                        }
                      />
                    </S.AssetWrapperFlex>
                  </>
                )}

                {assetType === 'hair' && (
                  <>
                    <S.AssetWrapper>
                      {assetList.hair.map(({ id, preview }) => (
                        <Asset
                          key={id}
                          id={id}
                          imageUrl={preview}
                          handleAsset={() => handleSetAsset(id)}
                        />
                      ))}
                    </S.AssetWrapper>
                    <S.AssetWrapperFlex direction='column'>
                      <HairColorPicker
                        colors={hairColors}
                        selectedColor={selectedHairColor}
                        onChange={(color) =>
                          handleChangeHairColor(color, setSelectedHairColor)
                        }
                      />
                    </S.AssetWrapperFlex>
                  </>
                )}
                {assetType === 'clothes' && (
                  <S.AssetWrapper>
                    {assetList.clothes.map(({ id, preview }) => (
                      <Asset
                        key={id}
                        id={id}
                        imageUrl={preview}
                        handleAsset={() => handleSetAsset(id)}
                      />
                    ))}
                  </S.AssetWrapper>
                )}
                {assetType === 'footwear' && (
                  <S.AssetWrapper>
                    {assetList.footwear.map(({ id, preview }) => (
                      <Asset
                        key={id}
                        id={id}
                        imageUrl={preview}
                        handleAsset={() => handleSetAsset(id)}
                      />
                    ))}
                  </S.AssetWrapper>
                )}
                {assetType === 'body' && (
                  <>
                    <S.AssetWrapper>
                      {bodyList.map(({ id, preview }) => (
                        <Asset
                          key={id}
                          id={id}
                          imageUrl={preview}
                          handleAsset={() => handleSetBody(id)}
                        />
                      ))}
                    </S.AssetWrapper>
                    <S.AssetWrapperFlex spacing={4}>
                      <CustomSlider
                        title='Head'
                        value={selectedHead}
                        onChange={(value) =>
                          handleChangeHeadProportions(value, setSelectedHead)
                        }
                      />
                      <CustomSlider
                        title='Arms'
                        value={selectedArms}
                        onChange={(value) =>
                          handleChangeArmsProportions(value, setSelectedArms)
                        }
                      />
                      <CustomSlider
                        title='Chest'
                        value={selectedChest}
                        onChange={(value) =>
                          handleChangeChestProportions(value, setSelectedChest)
                        }
                      />
                    </S.AssetWrapperFlex>
                  </>
                )}

                {assetType === 'etc' && (
                  <S.AssetWrapper
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '15px',
                    }}
                  >
                    <EyeColorPicker
                      selectedColor={selectedEyeColor}
                      onChange={(color) =>
                        handleChangeEyeColor(color, setSelectedEyeColor)
                      }
                    />
                    <CustomSlider
                      title='Skin Tone'
                      value={selectedSkinTone}
                      onChange={(value) =>
                        handleChangeSkinToneColor(value, setSelectedSkinTone)
                      }
                    />
                  </S.AssetWrapper>
                )}
              </div>
              <S.AssetTypeWrapper>
                {assetTypeArray.map((el) => (
                  <S.AssetTypeButton
                    key={el}
                    onClick={() => setAssetType(el)}
                    className={el === assetType ? 'on' : ''}
                  >
                    {el}
                  </S.AssetTypeButton>
                ))}
              </S.AssetTypeWrapper>
            </S.AssetContainer>
          </>
        )}
      </S.Container>
    </>
  );
}
